import React from 'react';
import { FaArrowLeft, FaSave, FaCheck, FaSpinner } from 'react-icons/fa';

export const inputClass =
  'w-full text-sm rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-3 py-2 text-gray-900 dark:text-white';
export const labelClass = 'block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1';

export const Field = ({ label, value, onChange, type = 'text', multiline = false, required = false }) => (
  <div>
    <label className={labelClass}>
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {multiline ? (
      <textarea
        className={inputClass}
        rows={2}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    ) : (
      <input
        type={type}
        className={inputClass}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    )}
  </div>
);

export const Section = ({ title, children }) => (
  <div className="space-y-3">
    <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 border-b border-gray-100 dark:border-dark-700 pb-2">
      {title}
    </h3>
    <div className="grid md:grid-cols-2 gap-3">{children}</div>
  </div>
);

export const ChecklistFormShell = ({
  title,
  subtitle,
  children,
  onCancel,
  onSaveDraft,
  onSubmit,
  isSubmitting,
  backLabel = 'Back',
  saveDraftLabel = 'Save Draft',
  submitLabel = 'Submit Checklist',
}) => (
  <div className="space-y-6 pb-20">
    <div className="card p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="page-title">{title}</h1>
          {subtitle && <p className="page-subtitle mt-1">{subtitle}</p>}
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 text-sm font-medium"
        >
          <FaArrowLeft className="w-3 h-3" />
          {backLabel}
        </button>
      </div>
      <div className="space-y-6">{children}</div>
      <div className="flex flex-wrap gap-3 mt-8 pt-6 border-t border-gray-100 dark:border-dark-700">
        <button
          type="button"
          onClick={onSaveDraft}
          disabled={isSubmitting}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 text-sm font-semibold disabled:opacity-50"
        >
          {isSubmitting ? <FaSpinner className="animate-spin" /> : <FaSave />}
          {saveDraftLabel}
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={isSubmitting}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-600 text-white text-sm font-semibold disabled:opacity-50"
        >
          {isSubmitting ? <FaSpinner className="animate-spin" /> : <FaCheck />}
          {submitLabel}
        </button>
      </div>
    </div>
  </div>
);

export const useChecklistForm = (initialData, defaultValues) => {
  const [formData, setFormData] = React.useState(() => ({
    ...defaultValues,
    ...(initialData || {}),
  }));

  const initialDataKey = React.useMemo(
    () => JSON.stringify(initialData || {}),
    [initialData]
  );

  React.useEffect(() => {
    setFormData({
      ...defaultValues,
      ...(initialData || {}),
    });
    // Only re-hydrate when saved answers change (e.g. after draft load), not on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDataKey]);

  const updateField = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));
  return [formData, setFormData, updateField];
};
